import {
  AspectRatio,
  CloseFullscreen,
  Delete,
  Edit,
  PriorityHigh,
  Restore,
  Undo,
} from "@mui/icons-material";
import { Card, CircularProgress, Grid, Icon, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import ApplicantFlags from "components/ApplicantFlags";
import ConfirmationModal from "components/ConfirmationModal";
import DataTableWithSubrows from "components/DataTableWithSubrows";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PictureAvatar from "components/PictureAvatar";
import { defaultReportModalValues } from "components/ReportModal/fixtures";
import SplitPanelSelector from "components/SplitPanelSelector";
import { useAppContextController } from "context/AppContext";
import useHandleBackButtonSubGrid from "hooks/useHandleBackButtonSubGrid";
import useSessionAuth from "hooks/useSessionAuth";
import fetchEventRosterForTimecardEditor from "layouts/pages/payroll/actions/fetchEventRosterForTimecardEditor";
import { cloneDeep, isArray } from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandManager } from "utils/classes/commandManager";
import { SaveTimecardDataCommand } from "utils/classes/saveTimecardDataCommand";
import { calculateTotalHours } from "utils/helpers/payrollHelper";
import { getCompanyImageUrl } from "utils/helpers/upload";
import useArrowKeysFieldSelection from "utils/useArrowKeysFieldSelection";
import useSort from "utils/useSort";
import createTimecardCorrection from "../../actions/createTimecardCorrection";
import exportPayrollBatch from "../../actions/exportPayrollBatch";
import fetchJobRosterForTimecardEditor from "../../actions/fetchJobRosterForTimecardEditor";
import getPayrollStatus from "../../actions/getPayrollStatus";
import resetPayrollBatch from "../../actions/resetPayrollBatch";
import savePayrollBatchRow from "../../actions/savePayrollBatchRow";
import submitTimecardBatch from "../../actions/submitTimecardBatch";
import AddDeductionsModal from "../AddDeductionsModal";
import EditableCellNumber from "../EditableCellNumber";
import EditableCellTime from "../EditableCellTime";
import TimecardEditorFilterCards from "../TimecardEditorFilterCards";
import { TimecardEditorPartnerMembersModal } from "./components/TimecardEditorPartnerMembersModal";
import TimecardEditorTableActions from "./components/TimecardEditorTableActions";

const useStyle = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible!important",
  },
  addButton: {
    fontSize: 40,
  },
});

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    whiteSpace: "pre-line",
  },
});

const defaultMinimizedPageSize = 10;

const TimecardEditorTable = ({
  title = "Timecard Editor",
  pageSize = 5,
  setFilters,
  navigateToAction = "",
  parentPage,
  parentFilters,
  parentNavigateWithBack,
  currentPeriod,
  fullscreen,
  panelOptions,
}) => {
  const queryClient = useQueryClient();
  const { action, id, eventUrl, jobSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const setNavigateUrl = useNavigate();
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const classes = useStyle();
  const {
    currentApplicant,
    setCurrentApplicant,
    currentVenue,
    venues,
    userType,
    currentLoggedUser,
    currentEvent,
    currentJob,
    company,
  } = useAppContextController();

  const isShiftJob = !!jobSlug;

  const allSearchParams = useMemo(() => {
    const allParams = {};
    const paramsArray = [];
    searchParams.entries().forEach(([key, value]) => {
      // Exclude "panel" query param
      if (key === "panel") return;

      if (searchParams.getAll(key).length > 1) {
        allParams[key] = encodeURIComponent(searchParams.getAll(key));
      } else {
        allParams[key] = encodeURIComponent(value);
      }
      paramsArray.push(`${key}=${allParams[key]}`);
    });
    return paramsArray;
  }, [searchParams]);

  const queryParamsHaveChangedRef = useRef(true);
  const [expandedIndexes, setExpandedIndexes] = useState({}); // Index of the collapsible item currently expanded
  const [modalInfo, setModalInfo] = useState({ data: currentApplicant });
  const [isBulkMessageModalOpen, toggleBulkMessageModal] = useState(false);
  const [isStaffingPoolModalOpen, toggleStaffingPoolModalOpen] = useState(false);
  const [openReportMessageModal, setOpenReportMessageModal] = useState(false);
  const [tableQueryDetails, setTableQueryDetails] = useState(null);
  const [chartQueryDetails, setChartQueryDetails] = useState(null);
  const [tableReportData, setTableReportData] = useState(null);
  const [chartReportData, setChartReportData] = useState(null);
  const [reportModalValues, setReportModalValues] = useState(defaultReportModalValues);
  const [commandManager, setCommandManager] = useState(() => new CommandManager());
  const [isPartnerMembersModalOpen, setPartnerMembersModalOpen] = useState(false);
  const [isResetConfirmationModalOpen, setResetConfirmationModalOpen] = useState(false);
  const [isSubmitConfirmationModalOpen, setSubmitConfirmationModalOpen] = useState(false);
  const [selectedRowForDeductions, setSelectedRowForDeductions] = useState(null);
  const [selectedRowForCorrection, setSelectedRowForCorrection] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showRosterData, setShowRosterData] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const d = new Date();
  d.setDate(d.getDate() + 2);
  const today = d.toISOString().split("T")[0];
  const eventDay = new Date(currentEvent?.eventDate).getTime() <= new Date(today).getTime();

  const { logoutAuth0User } = useSessionAuth();

  const [applicantsFilters, setApplicantsFilters] = useState({
    type: "",
  });

  const isPartners = applicantsFilters?.status === "Partner";

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // Default pageSize higher than 10. Don't use preferred pageSize

  const navigate = useNavigate();

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
      }/action/timecard${allSearchParams?.length ? `?${allSearchParams.join("&")}` : ""}`
    );
  };

  const minimize = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
      }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=timecard${
        allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
      }`
    );
  };

  // Handle back button from parent screens
  const { navigateWithBack } = useHandleBackButtonSubGrid({
    parentPage,
    parentFilters,
    setPage,
    setFilters: setApplicantsFilters,
    page,
    filters: applicantsFilters,
    navigateFunc: setNavigateUrl,
    navigateWithBackFunc: parentNavigateWithBack,
  });

  const handlePanelChange = useCallback(
    (e, panel) => {
      e.stopPropagation();
      if (["timecard", "payroll", "invoice"].includes(panel)) {
        navigate(
          `/payroll/${isShiftJob ? "job" : "event"}/${
            isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
          }/action/${panel}${allSearchParams?.length ? `?${allSearchParams.join("&")}` : ""}`
        );
        return;
      }
      navigate(
        `/payroll/${isShiftJob ? "job" : "event"}/${
          isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
        }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=${panel}${
          allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
        }`
      );
    },
    [allSearchParams, currentEvent?.eventUrl, currentJob?.jobSlug, isShiftJob, navigate]
  );

  const localPanelOptions = useMemo(() => {
    return panelOptions?.map((op) => ({
      ...op,
      hasShadow: op.name === "status" ? "emd" : "",
      fn: (e) => (op.name === "status" ? () => {} : handlePanelChange(e, op.name)),
    }));
  }, [handlePanelChange, panelOptions]);

  const { order, orderBy, toggleSort } = useSort("lastName", "asc");
  const options = useMemo(
    () => ({ page, limit: fullscreen ? limit : defaultMinimizedPageSize, order, orderBy }),
    [fullscreen, limit, order, orderBy, page]
  );
  const allOptions = useMemo(
    () => ({
      ...options,
      ...(isShiftJob
        ? {
            jobSlug: currentJob?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            filters: {
              ...applicantsFilters,
            },
          }),
    }),
    [
      applicantsFilters,
      currentEvent?.eventUrl,
      currentJob?.jobSlug,
      currentPeriod,
      isShiftJob,
      options,
    ]
  );

  const allOptionsPayrollStatus = useMemo(() => {
    return {
      ...(isShiftJob
        ? {
            jobSlug: currentJob?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }),
    };
  }, [currentEvent?.eventUrl, currentJob?.jobSlug, currentPeriod, isShiftJob]);

  useEffect(() => {
    return () => {
      // Clear all cached data
      queryClient.removeQueries({ queryKey: ["eventRosterList", allOptions] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetExpandedIndexes = useCallback((data) => {
    const newExpandedIndexes = {};

    if (!data?.length) return;

    for (let index = 0; index < data.length; index += 1) {
      const element = data[index];
      if (element?.subRows?.length) {
        newExpandedIndexes[index] = true;
      }
    }

    setExpandedIndexes(newExpandedIndexes);
  }, []);

  const {
    data: rosterList,
    isLoading,
    refetch: refetchRosterList,
  } = useQuery(
    ["eventRosterList", allOptions],
    () =>
      isShiftJob
        ? fetchJobRosterForTimecardEditor(allOptions)
        : fetchEventRosterForTimecardEditor(allOptions),
    {
      // cacheTime: 0,
      // staleTime: 0,
      enabled: isShiftJob ? !!currentJob?.jobSlug : !!currentEvent?.eventUrl,
      onSuccess: (data) => {
        if (queryParamsHaveChangedRef.current) {
          resetExpandedIndexes(data?.data?.data);
        }
        queryParamsHaveChangedRef.current = false;
      },
    }
  );

  const {
    data: payrollStatus,
    isLoading: isLoadingPayrollStatus,
    refetch: refetchPayrollStatus,
  } = useQuery(
    ["payrollStatus", allOptionsPayrollStatus],
    () => getPayrollStatus(allOptionsPayrollStatus),
    {}
  );

  const submitLocked = payrollStatus?.data?.timecardStatus === "Submitted";
  const payrollSubmitted = payrollStatus?.data?.payrollStatus === "Submitted";
  const correctionActive = payrollStatus?.data?.correctionActive;

  const eventIsInThePast =
    !isShiftJob &&
    currentEvent?.eventDate &&
    new Date(currentEvent.eventDate).toISOString() < new Date().toISOString();
  const wrongTimePeriod =
    !isShiftJob &&
    currentEvent?.eventDate &&
    (new Date(currentEvent.eventDate).toISOString() < currentPeriod?.[0]?.toISOString() ||
      new Date(currentEvent.eventDate).toISOString() > currentPeriod?.[1]?.toISOString());

  const stringifiedOptions = JSON.stringify(allOptions);
  useEffect(() => {
    setCommandManager(() => new CommandManager());
    resetExpandedIndexes(rosterList?.data?.data);
    queryParamsHaveChangedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedOptions]);

  const resetDataMutation = useMutation(resetPayrollBatch);
  const saveRowMutation = useMutation(savePayrollBatchRow);
  const exportPayrollMutation = useMutation(exportPayrollBatch);
  const createCorrectionMutation = useMutation(createTimecardCorrection);
  const submitDataMutation = useMutation(submitTimecardBatch, {
    onError: (e) => {
      const errorMessage =
        e?.response?.data?.message ?? "An error has occurred submitting the Timecard";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });

  // --------- ROSTER ---------
  const rosterApplicants = useMemo(() => {
    if (!rosterList?.data?.data?.length) return [];
    return rosterList.data.data.map((rItem) => rItem.rosterData);
  }, [rosterList?.data?.data]);
  // --------------------------

  useEffect(() => {
    if (rosterList?.data?.length && rosterList.data.length === 1) {
      // setSelectedId(applicants.data[0]._id);
      setCurrentApplicant(rosterList.data[0]);
    }
  }, [rosterList?.data]);

  useEffect(() => {
    if (action && rosterList?.data?.length) {
      const [setApplicantAction] = rosterList?.data?.filter((item) => item?._id === id);
      setCurrentApplicant(setApplicantAction);
    }
  }, [action, id, rosterList?.data]);

  const autosaveQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  // ------------ FIELD SWITCH ------------
  const inputRefs = useRef({});
  const getFinalFieldRowIndex = useCallback(
    (i, iSub) => {
      // If there are shift job items with subitems, we need to skip that index, as it doesn't have fields
      // Also, if there's an expanded shift job item, we need to increase the index by the number of items

      if (!rosterList?.data?.data?.length) return 0;

      // Check how many rows with subrows exist before the current index
      const newIndex = rosterList.data.data.reduce((prev, current, ind) => {
        if (ind > i) return prev;
        if (current?.subRows?.length) {
          // This row has subrows
          if (!expandedIndexes[ind]) {
            // If it's closed, skip it
            return prev;
          }
          if (expandedIndexes[i] && iSub != null) {
            // If it's open, and it's the current index, calculate the subrow index
            return prev + iSub;
          }
          // If it's open, but it's not the current index, increment the sum by the number of subitems
          return prev + current?.subRows?.length;
        }

        if (ind === i) return prev;
        return prev + 1;
      }, 0);

      return newIndex;
    },
    [expandedIndexes, rosterList?.data?.data]
  );
  const gridDimensions = useMemo(
    () => ({
      rows: getFinalFieldRowIndex(rosterList?.data?.data?.length ?? 0),
      cols: 4,
    }),
    [getFinalFieldRowIndex, rosterList?.data?.data?.length]
  );
  const { getFieldKey, handleKeyDown: handleKeyDownField } = useArrowKeysFieldSelection({
    inputRefs,
    gridDimensions,
  });
  // --------------------------------------

  const handleSelectPartnerMembers = (row) => {
    setSelectedPartner(row);
    setPartnerMembersModalOpen(true);
  };

  const handleClosePartnerMembersModal = () => {
    setSelectedPartner(null);
    setPartnerMembersModalOpen(false);
  };

  const handleToggleMinimizedInformation = () => {
    setShowRosterData((current) => !current);
  };

  // Update local cache of query data
  const updateDataCache = (applicantId, newData) => {
    const currentCacheData = queryClient.getQueryData(["eventRosterList", allOptions]);
    const dataCopy = cloneDeep(currentCacheData);
    const dataArray = dataCopy?.data?.data?.map((row) => {
      if (!row?.subRows?.length && row?._id === applicantId)
        return {
          ...row,
          rosterData: {
            ...row?.rosterData,
            ...newData,
          },
        };
      if (!!row?.subRows?.length && newData.punchId) {
        // We couldn't find a matching row in the main list, but the data includes a punchId
        // So this could be one of the subRows of a Shift Job row
        return {
          ...row,
          subRows: row.subRows.map((sr) => {
            if (sr.punchId === newData.punchId) {
              return {
                ...sr,
                rosterData: {
                  ...sr?.rosterData,
                  ...newData,
                },
              };
            }
            return sr;
          }),
        };
      }
      return row;
    });
    if (dataCopy?.data?.data) dataCopy.data.data = dataArray;
    queryClient.setQueryData(["eventRosterList", allOptions], dataCopy);
  };

  const pushChange = async (requestData, originalData) => {
    const command = new SaveTimecardDataCommand(
      autosaveQueueRef,
      isProcessingQueueRef,
      requestData,
      originalData,
      updateDataCache,
      saveRowMutation,
      false
    );
    commandManager.executeCommand(command);
  };

  // Undo function
  const handleUndo = useCallback(() => {
    if (commandManager.undo()) enqueueSnackbar("Last change undone", { variant: "success" });
    else enqueueSnackbar("Nothing to undo", { variant: "error" });
  }, [commandManager, enqueueSnackbar]);

  // Reset data function
  const handleReset = useCallback(async () => {
    const result = await resetDataMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            jobSlug: currentJob?.jobSlug,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Timecard reset successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      resetExpandedIndexes(rosterList?.data?.data);
      queryClient.removeQueries({ queryKey: ["eventRosterList", allOptions] });
      refetchRosterList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    allOptions,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    isShiftJob,
    queryClient,
    refetchPayrollStatus,
    refetchRosterList,
    resetDataMutation,
    resetExpandedIndexes,
    rosterList?.data?.data,
  ]);

  const handleConfirmCreateCorrection = useCallback(
    async (row) => {
      const result = await createCorrectionMutation.mutateAsync(
        isShiftJob && currentJob?.jobSlug
          ? {
              jobSlug: currentJob?.jobSlug,
              data: {
                applicantId: row?._id,
                timeIn: row?.rosterData?.timeIn,
                timeOut: row?.rosterData?.timeOut,
                payRate: row?.rosterData?.payRate,
                billRate: row?.rosterData?.billRate,
                deductionCode: row?.rosterData?.deductionCode,
                deductionAmount: row?.rosterData?.deductionAmount,
                deductionAmountType: row?.rosterData?.deductionAmountType,
                // deductionMaxAmount: newData.deductionMaxAmount,
                // deductionMaxAmountType: newData.deductionMaxAmountType,
                deductionSponsor: row?.rosterData?.deductionSponsor,
                startDate: currentPeriod?.[0],
                endDate: currentPeriod?.[1],
                timecardId: row?.punchId,
                salary: row?.rosterData?.salary,
                earningId: row?.rosterData?.earningId,
              },
            }
          : {
              eventUrl: currentEvent?.eventUrl,
              data: {
                applicantId: row?._id,
                timeIn: row?.rosterData?.timeIn,
                timeOut: row?.rosterData?.timeOut,
                payRate: row?.rosterData?.payRate,
                billRate: row?.rosterData?.billRate,
                deductionCode: row?.rosterData?.deductionCode,
                deductionAmount: row?.rosterData?.deductionAmount,
                deductionAmountType: row?.rosterData?.deductionAmountType,
                // deductionMaxAmount: newData.deductionMaxAmount,
                // deductionMaxAmountType: newData.deductionMaxAmountType,
                deductionSponsor: row?.rosterData?.deductionSponsor,
                startDate: currentPeriod?.[0],
                endDate: currentPeriod?.[1],
                salary: row?.rosterData?.salary,
                earningId: row?.rosterData?.earningId,
              },
            }
      );
      if (result?.data?.success) {
        enqueueSnackbar("Correction created successfully", { variant: "success" });
        setCommandManager(() => new CommandManager());
        queryClient.removeQueries({ queryKey: ["eventRosterList", allOptions] });
        refetchRosterList();
        refetchPayrollStatus();
      } else {
        enqueueSnackbar("An error has occurred", { variant: "success" });
      }
    },
    [
      allOptions,
      createCorrectionMutation,
      currentEvent?.eventUrl,
      currentJob?.jobSlug,
      currentPeriod,
      enqueueSnackbar,
      isShiftJob,
      queryClient,
      refetchPayrollStatus,
      refetchRosterList,
    ]
  );

  // Submit data function
  const handleSubmit = useCallback(async () => {
    const result = await submitDataMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            jobSlug: currentJob?.jobSlug,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Timecard submitted successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      queryClient.removeQueries({ queryKey: ["eventRosterList", allOptions] });
      refetchRosterList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    allOptions,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    isShiftJob,
    queryClient,
    refetchPayrollStatus,
    refetchRosterList,
    submitDataMutation,
  ]);

  // Submit data function
  const handleExport = useCallback(async () => {
    const result = await exportPayrollMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              jobSlugs: [currentJob?.jobSlug],
            },
          }
        : {
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              eventUrls: [currentEvent?.eventUrl],
            },
          }
    );
    if (result?.data?.success) {
      window.open(`${getCompanyImageUrl(company)}${result.data.exportUrl}`, "_blank");
      enqueueSnackbar("Timecard exported successfully", { variant: "success" });
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    company,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    exportPayrollMutation,
    isShiftJob,
  ]);

  // Toggle Shift Job sublist of rows
  const handleToggleSubList = useCallback(
    (index) => {
      if (expandedIndexes[index]) {
        setExpandedIndexes({ ...expandedIndexes, [index]: false });
      } else {
        setExpandedIndexes({ ...expandedIndexes, [index]: true });
      }
    },
    [expandedIndexes]
  );

  const onChangeField = (row, fieldName, fieldValue) => {
    // Find if there's an edit for the current field
    const date = isShiftJob
      ? moment(row?.timeIn).format("MM/DD/YY")
      : moment(currentEvent?.eventDate).format("MM/DD/YY");
    const editKey = `${row?._id}-${date}-${row?.punchId ?? ""}`;

    const originalData = {
      timeIn: row?.rosterData?.timeIn,
      timeOut: row?.rosterData?.timeOut,
      payRate: row?.rosterData?.payRate,
      billRate: row?.rosterData?.billRate,
      deductionCode: row?.rosterData?.deductionCode,
      deductionAmount: row?.rosterData?.deductionAmount,
      deductionAmountType: row?.rosterData?.deductionAmountType,
      // deductionMaxAmount: row?.rosterData?.deductionMaxAmount,
      // deductionMaxAmountType: row?.rosterData?.deductionMaxAmountType,
      deductionSponsor: row?.rosterData?.deductionSponsor,
      salary: row?.rosterData?.salary ?? "No",
      earningId: row?.rosterData?.earningId,
    };

    const newData = { ...originalData };
    if (isArray(fieldName)) {
      let hasChanges = false;
      for (let index = 0; index < fieldName.length; index += 1) {
        const fieldN = fieldName[index];
        const fieldV = fieldValue[index];

        newData[fieldN] = fieldV;

        // eslint-disable-next-line eqeqeq
        if ((fieldV ?? "") != (originalData[fieldN] ?? "")) hasChanges = true;
      }
      if (!hasChanges) return;
    } else {
      newData[fieldName] = fieldValue;

      // eslint-disable-next-line eqeqeq
      if ((fieldValue ?? "") == (originalData[fieldName] ?? "")) return;
    }

    if (isShiftJob && currentJob?.jobSlug)
      pushChange(
        {
          jobSlug: currentJob?.jobSlug,
          data: {
            applicantId: row?._id,
            timeIn: newData.timeIn,
            timeOut: newData.timeOut,
            payRate: newData.payRate,
            billRate: newData.billRate,
            deductionCode: newData.deductionCode,
            deductionAmount: newData.deductionAmount,
            deductionAmountType: newData.deductionAmountType,
            // deductionMaxAmount: newData.deductionMaxAmount,
            // deductionMaxAmountType: newData.deductionMaxAmountType,
            deductionSponsor: newData.deductionSponsor,
            salary: newData.salary,
            earningId: newData.earningId,
            startDate: currentPeriod?.[0],
            endDate: currentPeriod?.[1],
            timecardId: row?.punchId,
          },
        },
        originalData
      );
    else if (currentEvent?.eventUrl)
      pushChange(
        {
          eventUrl: currentEvent?.eventUrl,
          data: {
            applicantId: row?._id,
            timeIn: newData.timeIn,
            timeOut: newData.timeOut,
            payRate: newData.payRate,
            billRate: newData.billRate,
            deductionCode: newData.deductionCode,
            deductionAmount: newData.deductionAmount,
            deductionAmountType: newData.deductionAmountType,
            // deductionMaxAmount: newData.deductionMaxAmount,
            // deductionMaxAmountType: newData.deductionMaxAmountType,
            deductionSponsor: newData.deductionSponsor,
            salary: newData.salary,
            earningId: newData.earningId,
            startDate: currentPeriod?.[0],
            endDate: currentPeriod?.[1],
          },
        },
        originalData
      );
  };

  const handleAddDeduction = (row, deduction) => {
    onChangeField(
      row,
      [
        "deductionCode",
        "deductionAmount",
        "deductionAmountType",
        // "deductionMaxAmount",
        // "deductionMaxAmountType",
        "deductionSponsor",
      ],
      [
        deduction?.code,
        deduction?.amount,
        deduction?.amountType,
        // deduction?.maxAmount,
        // deduction?.maxAmountType,
        deduction?.sponsor,
      ]
    );
  };

  // Keyboard shortcut for UNDO
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any input field is focused
      // eslint-disable-next-line prefer-destructuring
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true" ||
          activeElement.isContentEditable);

      // Only handle shortcuts when no input is focused
      if (!isInputFocused && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && !event.shiftKey && !isPartnerMembersModalOpen) {
          event.preventDefault();
          handleUndo();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, isPartnerMembersModalOpen]);

  const validatePayBillRate = (newVal, compareVal, field) => {
    let result;
    if (!newVal) return true;
    if (field === "billRate") {
      result =
        compareVal == null ||
        compareVal === "" ||
        parseFloat(newVal.toString()) >= parseFloat(compareVal.toString());
    } else {
      result =
        compareVal == null ||
        compareVal === "" ||
        parseFloat(newVal.toString()) <= parseFloat(compareVal.toString());
    }
    if (!result) enqueueSnackbar("Bill rate must be larger than Pay Rate", { variant: "error" });
    return result;
  };

  const columns = [
    ...(fullscreen
      ? [
          {
            title: "Avatar",
            field: "profileImg",
            width: 100,
            sortable: false,
            skipForSubtable: true,
            customCell: (image, field, row) => (
              <PictureAvatar
                image={row?.profileImg}
                firstName={row?.firstName}
                lastName={row?.lastName}
                userId={row?.userId ?? row.userRecordId ?? row?._id}
                size="md"
              />
            ),
          },
          {
            title: "Flag",
            field: "recordFlagged",
            width: 100,
            sortable: false,
            skipForSubtable: true,
            customCell: (flagged, field, row) => (
              <ApplicantFlags flagged={flagged} row={row} onRemoveFlag={() => {}} />
            ),
          },
          {
            title: "Status",
            field: "employmentStatus",
            skipForSubtable: true,
          },
          {
            title: "Employee ID",
            field: "employeeID",
            skipForSubtable: true,
          },
        ]
      : []),
    {
      title: "Last Name",
      field: "lastName",
      skipForSubtable: true,
      customCell: (name, field, row) =>
        row ? (
          <CustomWidthTooltip
            title={`${row?.email || ""} \n ${row?.city || ""} ${row?.state || ""}`}
            sx={{ color: "red" }}
          >
            <div>{name}</div>
          </CustomWidthTooltip>
        ) : null,
    },
    {
      title: "First Name",
      field: "firstName",
      skipForSubtable: true,
      customCell: (name, field, row) =>
        row ? (
          <CustomWidthTooltip
            title={`${row?.email || ""} \n ${row?.city || ""} ${row?.state || ""}`}
            sx={{ color: "red" }}
          >
            <div>{name}</div>
          </CustomWidthTooltip>
        ) : null,
    },
    ...(fullscreen || !showRosterData
      ? [
          ...(isShiftJob
            ? [
                {
                  title: "Shift Name",
                  field: "shiftSlug",
                  customCell: (shiftSlug, field, row) => {
                    const jobShifts = currentJob?.shifts ?? [];
                    const matchingShift = jobShifts.find((s) => s.slug === shiftSlug);

                    if (matchingShift) return matchingShift.shiftName;
                    return shiftSlug;
                  },
                },
              ]
            : [
                {
                  title: "Type",
                  field: "type",
                  skipForSubtable: true,
                },
              ]),
          {
            title: "Position",
            field: "primaryPosition",
            sortable: false,
            customCell: (position, field, row) => {
              if (isShiftJob) return currentJob?.title;
              const DEFAULT_POSITION = "Event Staff";

              return row?.rosterData?.primaryPosition || DEFAULT_POSITION;
            },
          },
          {
            title: "Work Date",
            field: "rosterData",
            sortable: false,
            customCell: (rosterData, field, row) =>
              isShiftJob
                ? moment(row?.timeIn).format("MM/DD/YY")
                : moment(currentEvent?.eventDate).format("MM/DD/YY"),
          },
        ]
      : []),
    ...(fullscreen || showRosterData
      ? [
          {
            title: "Time In",
            field: "rosterData",
            width: 150,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 3 }}>-</MDTypography>;
              }

              if (payrollSubmitted || (correctionActive && !row?.rosterData?.isCorrection)) {
                return (
                  <span>{`${
                    row?.rosterData?.timeOut ? moment(row?.rosterData?.timeIn).format("hh:mma") : ""
                  }`}</span>
                );
              }

              return (
                <EditableCellTime
                  eventDate={
                    isShiftJob
                      ? row?.rosterData?.timeIn ?? row?.rosterData?.timeOut
                      : currentEvent?.eventDate
                  }
                  time={row?.rosterData?.timeIn ?? ""}
                  setTime={(val) => onChangeField(row, "timeIn", val ? val.toISOString() : val)}
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 0)}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 0)] = el;
                    }
                  }}
                  disableClear={isShiftJob}
                  fieldName="Time In"
                />
              );
            },
          },
          {
            title: "Time Out",
            field: "rosterData",
            width: 150,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 3 }}>-</MDTypography>;
              }

              if (payrollSubmitted || (correctionActive && !row?.rosterData?.isCorrection)) {
                return (
                  <span>{`${
                    row?.rosterData?.timeOut
                      ? moment(row?.rosterData?.timeOut).format("hh:mma")
                      : ""
                  }`}</span>
                );
              }

              return (
                <EditableCellTime
                  eventDate={
                    isShiftJob
                      ? row?.rosterData?.timeIn ?? row?.rosterData?.timeOut
                      : currentEvent?.eventDate
                  }
                  time={row?.rosterData?.timeOut ?? ""}
                  setTime={(val) => onChangeField(row, "timeOut", val ? val.toISOString() : val)}
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 1)}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 1)] = el;
                    }
                  }}
                  fieldName="Time Out"
                />
              );
            },
          },
          {
            title: "Pay Rate",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              if (payrollSubmitted || (correctionActive && !row?.rosterData?.isCorrection)) {
                return <span>{`$${row?.rosterData?.payRate || "0"}`}</span>;
              }

              return (
                <EditableCellNumber
                  amount={row?.rosterData?.payRate ?? ""}
                  setAmount={(val) => onChangeField(row, "payRate", val ? parseFloat(val) : val)}
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 2)}
                  // onValidate={(val) =>
                  //   validatePayBillRate(val, row?.rosterData?.billRate ?? "", "payRate")
                  // }
                  adornmentSymbol="$"
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 2)] = el;
                    }
                  }}
                />
              );
            },
          },
          {
            title: "Bill Rate",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              if (payrollSubmitted || (correctionActive && !row?.rosterData?.isCorrection)) {
                return <span>{`$${row?.rosterData?.billRate || "0"}`}</span>;
              }

              return (
                <EditableCellNumber
                  amount={row?.rosterData?.billRate ?? ""}
                  setAmount={(val) => onChangeField(row, "billRate", val ? parseFloat(val) : val)}
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 3)}
                  // onValidate={(val) =>
                  //   validatePayBillRate(val, row?.rosterData?.payRate ?? "", "billRate")
                  // }
                  adornmentSymbol="$"
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 3)] = el;
                    }
                  }}
                />
              );
            },
          },
          {
            title: "Deduc.",
            field: "rosterData",
            width: 80,
            sortable: false,
            customCell: (rosterData, field, row) => {
              return row?.slug || row?.subRows?.length ? (
                <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>
              ) : (
                <Typography
                  variant="text"
                  sx={{
                    color: `${
                      // eslint-disable-next-line no-nested-ternary
                      parseFloat(rosterData?.deductionAmount ?? 0) > 0
                        ? "success"
                        : parseFloat(rosterData?.deductionAmount ?? 0) < 0
                        ? "error"
                        : "dark"
                    }.main`,
                  }}
                >
                  {`(${(rosterData?.deductionAmount || 0) < 0 ? "-" : ""}${
                    rosterData?.deductionAmountType === "flat" ? "$" : ""
                  }${Math.abs(rosterData?.deductionAmount || 0)}${
                    rosterData?.deductionAmountType === "percentage" ? "%" : ""
                  })`}
                </Typography>
              );
            },
          },
        ]
      : []),
    {
      title: "Total Hrs.",
      field: "rosterData",
      width: 120,
      sortable: false,
      customCell: (rosterData, field, row) => {
        let hourDifference = 0;
        let hours = calculateTotalHours(row, currentEvent?.timeZone);
        if (correctionActive && row?.rosterData?.isCorrection) {
          // This row is corrected. Display difference of hours
          const originalHours = calculateTotalHours(
            {
              ...row,
              rosterData: {
                ...row?.rosterData,
                timeIn: row?.rosterData?.originalTimeIn,
                timeOut: row?.rosterData?.originalTimeOut,
              },
            },
            currentEvent?.timeZone
          );
          hourDifference = hours - originalHours;
          hourDifference = Math.round(hourDifference * 100) / 100;
        }
        if (row?.subRows?.length) {
          hours = row.subRows.reduce((prev, current, i, array) => {
            return (
              prev +
              calculateTotalHours(
                {
                  ...current,
                  rosterData: {
                    ...current?.rosterData,
                    timeIn: current?.rosterData?.originalTimeIn ?? current?.rosterData?.timeIn,
                    timeOut: current?.rosterData?.originalTimeOut ?? current?.rosterData?.timeOut,
                  },
                },
                currentEvent?.timeZone
              )
            );
          }, 0);
        }
        if (row?.members?.length) {
          hours = row.members.reduce((prev, current) => {
            return (
              prev +
              calculateTotalHours(
                {
                  ...current,
                  rosterData: {
                    ...current?.rosterData,
                    timeIn: current?.rosterData?.originalTimeIn ?? current?.rosterData?.timeIn,
                    timeOut: current?.rosterData?.originalTimeOut ?? current?.rosterData?.timeOut,
                  },
                },
                currentEvent?.timeZone
              )
            );
          }, 0);
        }

        const hoursRounded = Math.round(hours * 100) / 100;
        const hourDifferenceSign = hourDifference >= 0 ? "+" : "-";

        return `${hoursRounded}${
          correctionActive && row?.rosterData?.isCorrection
            ? ` (${hourDifferenceSign}${Math.abs(hourDifference)})`
            : ""
        }`;
      },
    },
    {
      title: "Actions",
      field: "_id",
      width: 150,
      sortable: false,
      customCell: (rowId, field, row, ind, iSub) => (
        <TimecardEditorTableActions
          id={rowId}
          row={row}
          setNavigateUrl={navigateWithBack}
          isShiftJob={isShiftJob}
          isExpanded={expandedIndexes[ind]}
          onToggleSubList={() => handleToggleSubList(ind)}
          onOpenPartnerMembers={handleSelectPartnerMembers}
          deductionsOnly={iSub != null}
          onSelectRowForDeductions={setSelectedRowForDeductions}
          editLocked={payrollSubmitted}
          onSelectRowForCorrection={setSelectedRowForCorrection}
          correctionActive={correctionActive}
        />
      ),
    },
  ];

  return (
    <>
      <Card
        className={classes.box}
        sx={
          fullscreen
            ? undefined
            : {
                height: 850,
              }
        }
      >
        <Grid container justifyContent="space-between" alignItems="flex-start" flexWrap="nowrap">
          <MDBox>
            <Grid container spacing={3} alignItems="center" justifyContent="space-between" py={1}>
              <Grid container item justifyContent="space-between">
                <MDBox display="flex">
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="error"
                    color="white"
                    borderRadius="xl"
                    ml={3}
                    mt={-4}
                  >
                    <Icon fontSize="large">schedule</Icon>
                  </MDBox>
                  <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
                    {title}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>

            <MDBox sx={{ ml: 3, mb: 2, maxWidth: fullscreen ? "unset" : "600px" }}>
              {payrollStatus?.data?.timecardStatus === "Submitted" &&
                !payrollSubmitted &&
                !correctionActive && (
                  <Grid container item alignItems="center" flexWrap="nowrap">
                    <PriorityHigh
                      color="white"
                      fontSize="medium"
                      sx={{
                        mr: 1,
                        padding: 0.3,
                        backgroundColor: "warning.main",
                        borderRadius: "50%",
                      }}
                    />
                    <MDTypography variant="body" color="warning">
                      This Timecard has already been submitted, but further changes can be made.
                    </MDTypography>
                  </Grid>
                )}
              {payrollSubmitted && !correctionActive && (
                <Grid container item alignItems="center" flexWrap="nowrap">
                  <PriorityHigh
                    color="white"
                    fontSize="medium"
                    sx={{
                      mr: 1,
                      padding: 0.3,
                      backgroundColor: "error.main",
                      borderRadius: "50%",
                    }}
                  />
                  <MDTypography variant="body" color="error">
                    The Payroll has already been submitted. Create a correction to modify the
                    Timecard.
                  </MDTypography>
                </Grid>
              )}
              {correctionActive && (
                <Grid container item alignItems="center" flexWrap="nowrap">
                  <Edit
                    color="white"
                    fontSize="medium"
                    sx={{
                      mr: 1,
                      padding: 0.3,
                      backgroundColor: "warning.main",
                      borderRadius: "50%",
                    }}
                  />
                  <MDTypography variant="body" color="warning">
                    Correction in progress
                  </MDTypography>
                </Grid>
              )}
            </MDBox>

            {!isShiftJob && (
              <MDBox ml={3}>
                <MDBox display="flex" flexWrap="wrap" alignItems="center">
                  <TimecardEditorFilterCards
                    setPage={setPage}
                    parent="Venues"
                    setFilters={setApplicantsFilters}
                    filters={{ ...applicantsFilters }}
                    doReplace
                    eventUrl={currentEvent?.eventUrl}
                  />
                </MDBox>
              </MDBox>
            )}
          </MDBox>
          <MDBox sx={{ paddingTop: 1, paddingRight: 2 }}>
            <MDBox sx={{ display: "flex", alignItems: "center" }}>
              {saveRowMutation.isLoading && (
                <>
                  <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                    Saving
                  </MDTypography>
                  <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
                </>
              )}
              {fullscreen && (
                <MDBox display="flex" justifyContent="center" alignItems="center" mr={2}>
                  <SplitPanelSelector
                    panelOptions={localPanelOptions}
                    selectedPanel="timecard"
                    isEdit
                  />
                </MDBox>
              )}
              {!fullscreen && (
                <Tooltip title={showRosterData ? "Hide Fields" : "Display Fields"}>
                  <span>
                    <IconButton
                      color={showRosterData ? "success" : "secondary"}
                      onClick={handleToggleMinimizedInformation}
                    >
                      <Edit fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={correctionActive ? "Cancel Correction" : "Reset"}>
                <span>
                  <IconButton
                    color="error"
                    onClick={() => setResetConfirmationModalOpen(true)}
                    disabled={isLoading || payrollSubmitted || wrongTimePeriod}
                  >
                    {correctionActive ? <Delete fontSize="large" /> : <Restore fontSize="large" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    color="info"
                    onClick={handleUndo}
                    disabled={isLoading || payrollSubmitted || wrongTimePeriod}
                  >
                    <Undo fontSize="large" />
                  </IconButton>
                </span>
              </Tooltip>
              {!fullscreen && (
                <Tooltip title="Go Fullscreen">
                  <span>
                    <IconButton color="info" onClick={toggleFullscreen}>
                      <AspectRatio fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {fullscreen && (
                <Tooltip title="Minimize">
                  <span>
                    <IconButton color="info" onClick={minimize}>
                      <CloseFullscreen fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </MDBox>
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                mt: 2,
                gap: 2,
              }}
            >
              <MDButton
                onClick={handleExport}
                color="success"
                variant="contained"
                disabled={
                  isLoading ||
                  submitDataMutation.isLoading ||
                  isLoadingPayrollStatus ||
                  saveRowMutation.isLoading ||
                  wrongTimePeriod
                }
              >
                Export
              </MDButton>
              <MDButton
                onClick={() => setSubmitConfirmationModalOpen(true)}
                color="info"
                variant="contained"
                disabled={
                  isLoading ||
                  submitDataMutation.isLoading ||
                  isLoadingPayrollStatus ||
                  payrollSubmitted ||
                  submitLocked ||
                  saveRowMutation.isLoading ||
                  wrongTimePeriod
                }
              >
                Submit
              </MDButton>
            </MDBox>
          </MDBox>
        </Grid>
        <MDBox pt={2} pb={2} pl={!fullscreen ? 2 : 0} pr={!fullscreen ? 2 : 0}>
          {wrongTimePeriod && (
            <MDBox sx={{ mt: 1, mb: 4 }}>
              <MDTypography variant="h4" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                Wrong Period
              </MDTypography>
              <MDTypography variant="h5" color="secondary" sx={{ ml: 2, textAlign: "center" }}>
                {`This event ${
                  eventIsInThePast ? "took" : "takes"
                } place in a different time period.`}
              </MDTypography>
            </MDBox>
          )}
          {!wrongTimePeriod && (
            <DataTableWithSubrows
              columns={columns}
              data={rosterList?.data ?? []}
              page={page}
              limit={fullscreen ? limit : defaultMinimizedPageSize}
              setPage={setPage}
              setLimit={setLimit}
              isLoading={isLoading}
              // onRowClick={isNotClientOrEventAdmin ? (row, e) => handleRowClick(row, e) : () => {}}
              order={order}
              orderBy={orderBy}
              toggleSort={toggleSort}
              fetchAll={false}
              defaultSort="lastName:asc"
              idField="_id"
              {...(fullscreen ? {} : { height: 610 })}
              hidePageSizeSelector={!fullscreen}
              stickyHeader={!fullscreen}
              openIndexes={expandedIndexes}
            />
          )}
        </MDBox>
      </Card>
      {isPartnerMembersModalOpen && (
        <TimecardEditorPartnerMembersModal
          isOpen={isPartnerMembersModalOpen}
          toggleModal={handleClosePartnerMembersModal}
          partner={selectedPartner}
          eventDay={eventDay}
          saveRowMutation={saveRowMutation}
          navigateWithBack={navigateWithBack}
          currentPeriod={currentPeriod}
          editLocked={payrollSubmitted}
          handleConfirmCreateCorrection={handleConfirmCreateCorrection}
          correctionActive={correctionActive}
        />
      )}
      {!!selectedRowForDeductions && (
        <AddDeductionsModal
          open={!!selectedRowForDeductions}
          setOpen={() => {
            setSelectedRowForDeductions(null);
          }}
          onAddDeduction={handleAddDeduction}
          row={selectedRowForDeductions}
        />
      )}
      {isResetConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isResetConfirmationModalOpen}
          onClose={() => {
            setResetConfirmationModalOpen(false);
          }}
          title={correctionActive ? "Cancel Correction" : "Reset data"}
          body={
            correctionActive
              ? "Are you sure you want to cancel the correction? This action cannot be undone."
              : "Are you sure you want to reset the data? This will undo all changes made to this timecard and restore the original values. This action cannot be undone."
          }
          onConfirm={(e) => {
            e.stopPropagation();
            handleReset();
            setResetConfirmationModalOpen(false);
          }}
        />
      )}
      {isSubmitConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isSubmitConfirmationModalOpen}
          onClose={() => {
            setSubmitConfirmationModalOpen(false);
          }}
          title="Submit Timecard"
          body="Are you sure you want to submit this timecard? This will lock the timecard and apply all the changes made in the editor."
          onConfirm={(e) => {
            e.stopPropagation();
            handleSubmit();
            setSubmitConfirmationModalOpen(false);
          }}
        />
      )}
      {!!selectedRowForCorrection && (
        <ConfirmationModal
          isOpen={!!selectedRowForCorrection}
          onClose={() => {
            setSelectedRowForCorrection(null);
          }}
          title="Create correction"
          body="Are you sure you want to create a correction for this row?"
          onConfirm={(e) => {
            e.stopPropagation();
            handleConfirmCreateCorrection(selectedRowForCorrection);
            setSelectedRowForCorrection(null);
          }}
        />
      )}
    </>
  );
};

// Setting default values for the props
TimecardEditorTable.defaultProps = {
  // currentVenue: {},
  title: "Applicants",
};

// Typechecking props
TimecardEditorTable.propTypes = {
  // currentVenue: PropTypes.object,
  title: PropTypes.string,
};

export default TimecardEditorTable;
